import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle, FaPlus, FaTimes, FaComments } from "react-icons/fa";
import api from "../../services/api";
import Pusher from "pusher-js";
import "./admin.css";

const PUSHER_KEY = "540d521fe5fe005cb99d";
const PUSHER_CLUSTER = "ap2";

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Create flow
  const [createStep, setCreateStep] = useState(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);

  // Detail view
  const [detailTeam, setDetailTeam] = useState(null);

  // Edit modal
  const [editingTeam, setEditingTeam] = useState(null);
  const [editName, setEditName] = useState("");

  // Add member modal
  const [addingToTeam, setAddingToTeam] = useState(null);
  const [memberSearch, setMemberSearch] = useState("");

  // Chat state
  const [chatTeam, setChatTeam] = useState(null);

  const token = localStorage.getItem("token");
  const [searchParams, setSearchParams] = useSearchParams();
  const chatTeamParam = searchParams.get("chatTeam");

  const fetchTeams = async () => {
    try {
      const res = await api.get("/admin/teams", { headers: { Authorization: `Bearer ${token}` } });
      setTeams(res.data.teams || []);
    } catch (e) { console.error(e); }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/admin/get_employees", { headers: { Authorization: `Bearer ${token}` } });
      const onlyEmployees = (res.data.employees || []).filter(emp => emp.role === 'employee');
      setEmployees(onlyEmployees);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchTeams(); fetchEmployees(); }, []);

  // Restore chatTeam from URL parameter after teams load
  useEffect(() => {
    if (chatTeamParam && teams.length > 0) {
      const team = teams.find(t => t.id == chatTeamParam);
      if (team) {
        setChatTeam(team);
      } else {
        // Team not found – clear the parameter
        setSearchParams({});
      }
    } else if (!chatTeamParam) {
      setChatTeam(null);
    }
  }, [chatTeamParam, teams, setSearchParams]);

  // When a chat is opened, update the URL
  const openChat = (team) => {
    setChatTeam(team);
    setSearchParams({ chatTeam: team.id });
  };

  // When chat is closed, remove the URL parameter
  const closeChat = () => {
    setChatTeam(null);
    setSearchParams({});
  };

  const employeeTeamMap = {};
  teams.forEach(team => {
    if (team.member_ids) {
      team.member_ids.forEach(userId => {
        employeeTeamMap[userId] = team.name;
      });
    }
  });

  // ── Create team ──
  const startCreate = () => { setCreateStep('name'); setNewTeamName(''); setSelectedMembers([]); };
  const nextToMembers = () => {
    if (!newTeamName.trim()) return setError("Team name required");
    setCreateStep('members');
  };
  const saveTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const createRes = await api.post("/admin/teams/create", { name: newTeamName }, { headers: { Authorization: `Bearer ${token}` } });
      const teamId = createRes.data.team_id;
      for (let userId of selectedMembers) {
        await api.post("/admin/teams/members/add", { team_id: teamId, user_id: userId }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSuccess("Team created!");
      setTimeout(() => setSuccess(""), 3000);
      setCreateStep(null);
      fetchTeams();
    } catch (e) { setError(e.response?.data?.message || "Failed to create team"); }
  };
  const toggleMemberSelection = (userId) => {
    setSelectedMembers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  // ── Edit team name ──
  const openEdit = (team) => { setEditingTeam(team); setEditName(team.name); };
  const saveEdit = async () => {
    if (!editingTeam || !editName.trim()) return;
    try {
      await api.put("/admin/teams/update", { team_id: editingTeam.id, name: editName }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Team updated!");
      setTimeout(() => setSuccess(""), 3000);
      setEditingTeam(null);
      fetchTeams();
    } catch (e) { setError(e.response?.data?.message || "Failed to update team"); }
  };

  // ── Delete team ──
  const deleteTeam = async (teamId) => {
    if (!window.confirm("Delete this team?")) return;
    try {
      await api.delete(`/admin/teams/delete?team_id=${teamId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Team deleted");
      setTimeout(() => setSuccess(""), 3000);
      if (detailTeam && detailTeam.id === teamId) setDetailTeam(null);
      if (chatTeam && chatTeam.id === teamId) closeChat();
      fetchTeams();
    } catch (e) { setError(e.response?.data?.message || "Failed to delete team"); }
  };

  // ── Remove member ──
  const removeMember = async (userId) => {
    if (!detailTeam) return;
    if (!window.confirm("Remove this member?")) return;
    try {
      await api.delete(`/admin/teams/members/remove?team_id=${detailTeam.id}&user_id=${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Member removed");
      setTimeout(() => setSuccess(""), 2000);
      setDetailTeam(prev => ({
        ...prev,
        member_ids: prev.member_ids.filter(id => id !== userId),
        member_count: (prev.member_count || 0) - 1
      }));
      fetchTeams();
    } catch (e) { setError(e.response?.data?.message || "Failed to remove member"); }
  };

  // ── Add member ──
  const openAddMember = (team) => { setAddingToTeam(team); setMemberSearch(""); };
  const addMemberToTeam = async (userId) => {
    if (!addingToTeam) return;
    try {
      await api.post("/admin/teams/members/add", { team_id: addingToTeam.id, user_id: userId }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess("Member added");
      setTimeout(() => setSuccess(""), 2000);
      if (detailTeam && detailTeam.id === addingToTeam.id) {
        setDetailTeam(prev => ({
          ...prev,
          member_ids: [...prev.member_ids, userId],
          member_count: (prev.member_count || 0) + 1
        }));
      }
      fetchTeams();
    } catch (e) { setError(e.response?.data?.message || "Failed to add member"); }
  };

  const backToList = () => setDetailTeam(null);

  // ==========================================
  //   INLINE CHAT FOR ADMIN (with persistence)
  // ==========================================
  const [chatMessages, setChatMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loadingChat, setLoadingChat] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Fetch messages when chatTeam changes – with safety guard
  useEffect(() => {
    if (!chatTeam || !chatTeam.id) return;          // ✅ wait until we have a valid team
    setLoadingChat(true);
    api.get(`/user/team_messages?team_id=${chatTeam.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setChatMessages(res.data.messages || []);
      })
      .catch(err => {
        console.error(err);
        setError("Failed to load messages");
      })
      .finally(() => setLoadingChat(false));

    // Pusher subscription
    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    const channel = pusher.subscribe(`team-${chatTeam.id}`);
    channel.bind("new-message", (newMsg) => {
      setChatMessages(prev => [...prev, newMsg]);
    });
    return () => {
      pusher.unsubscribe(`team-${chatTeam.id}`);
      pusher.disconnect();
    };
  }, [chatTeam, token]);

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
  useEffect(() => { scrollToBottom(); }, [chatMessages]);

  const sendTeamMessage = async () => {
    const text = newMsg.trim();
    const file = selectedFile;
    if (!text && !file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("team_id", chatTeam.id);
      if (file) {
        formData.append("file", file);
        formData.append("message", text);
        await api.post("/user/team_send", formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        clearFile();
      } else {
        await api.post("/user/team_send", { team_id: chatTeam.id, message: text }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setNewMsg("");
    } catch (err) { setError(err.response?.data?.message || "Failed to send"); }
    finally { setSending(false); }
  };

  const clearFile = () => { setSelectedFile(null); setFilePreview(""); if (fileInputRef.current) fileInputRef.current.value = ""; };
  const handleFileSelect = (e) => { const file = e.target.files?.[0]; if (file) { setSelectedFile(file); setFilePreview(file.name); } };

  // If chatTeam is active, render the chat UI
  if (chatTeam) {
    return (
      <div className="admin-container">
        <div className="chat-container" style={{ height: "85vh" }}>
          <div className="chat-header">
            <button className="back-btn" onClick={closeChat}><FaArrowLeft style={{ marginRight: "6px" }} /> Back</button>
            <span># {chatTeam.name}</span>
            <button className="chat-close-btn" onClick={closeChat} title="Close chat" aria-label="Close chat">
              <FaTimes />
            </button>
          </div>
          <div className="chat-messages">
            {loadingChat && <div className="loading-spinner"></div>}
            {chatMessages.map(msg => (
              <div key={msg.id} className={`message ${msg.sender_id == user.id ? "sent" : "received"}`}>
                {msg.sender_name && <small className="sender">{msg.sender_name}</small>}
                {msg.message && <p>{msg.message}</p>}
                {msg.file_path && (
                  <a href={`https://dt-attendance-system.desired-techs.com/dt-backend/${msg.file_path}`}
                     target="_blank" rel="noreferrer" className="chat-file-link" download>
                    <FaPaperclip style={{ marginRight: "6px" }} /> {msg.file_path.split('/').pop()}
                  </a>
                )}
                <span className="time">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {selectedFile && (
            <div className="file-preview-bar">
              <span><FaPaperclip style={{ marginRight: "6px" }} /> {filePreview}</span>
              <button onClick={clearFile} disabled={sending} aria-label="Remove attachment"><FaTimes /></button>
            </div>
          )}
          <div className="chat-input">
            <input type="text" placeholder="Type a message..." value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendTeamMessage()}
              disabled={sending} />
            <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
            <button className="btn-attach" onClick={() => fileInputRef.current?.click()} disabled={sending} aria-label="Attach file">
              <FaPaperclip />
            </button>
            {sending ? <span className="uploading-text">Sending…</span> : <button onClick={sendTeamMessage} className="btn-submit">Send</button>}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  // ── Detail view (unchanged) ──
  if (detailTeam) {
    const teamMembers = detailTeam.member_ids
      ? detailTeam.member_ids.map(id => employees.find(e => e.id === id)).filter(Boolean)
      : [];

    return (
      <div className="admin-container">
        <div className="header-banner mb-4">
          <div className="header-content">
            <div>
              <h1 className="header-title">Team: {detailTeam.name}</h1>
              <p className="header-subtitle">{detailTeam.member_count || 0} members</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn-add-student" onClick={backToList}><FaArrowLeft style={{ marginRight: "6px" }} /> Back to Teams</button>
              <button className="btn-add-student" onClick={() => openAddMember(detailTeam)}>
                <span className="btn-icon"><FaPlus /></span> Add Member
              </button>
            </div>
          </div>
        </div>

        {error && <div className="error-message"><FaTimesCircle style={{ marginRight: "6px" }} />{error}</div>}
        {success && <div className="success-message"><FaCheckCircle style={{ marginRight: "6px" }} />{success}</div>}

        <div className="students-card">
          <div className="card-header-custom">
            <h3 className="card-title">Members</h3>
          </div>
          <div className="table-wrapper">
            <table className="students-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Field</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.length === 0 ? (
                  <tr><td colSpan="5" className="empty-state">No members</td></tr>
                ) : (
                  teamMembers.map((emp, idx) => (
                    <tr key={emp.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">{emp.employeeName?.charAt(0) || 'E'}</div>
                          <span>{emp.employeeName}</span>
                        </div>
                      </td>
                      <td><span className="field-badge">{emp.employee_fields || '—'}</span></td>
                      <td><span className="role-badge role-employee">Employee</span></td>
                      <td>
                        <button className="btn-action btn-delete" onClick={() => removeMember(emp.id)}>Remove</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Member Modal (unchanged) */}
        {addingToTeam && (
          <div className="modal-overlay" onClick={() => setAddingToTeam(null)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-custom">
                <h3 className="modal-title">Add Member to {addingToTeam.name}</h3>
                <button className="modal-close" onClick={() => setAddingToTeam(null)} aria-label="Close add member modal"><FaTimes /></button>
              </div>
              <div className="modal-body-custom">
                <div className="search-box mb-3">
                  <span className="search-icon"><FaSearch /></span>
                  <input type="text" placeholder="Search employees..." className="search-input"
                    value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
                </div>
                <div className="member-check-list">
                  {employees
                    .filter(emp => emp.employeeName?.toLowerCase().includes(memberSearch.toLowerCase()))
                    .map(emp => {
                      const alreadyInTeam = detailTeam.member_ids?.includes(emp.id);
                      return (
                        <div key={emp.id} className="member-item" style={{ justifyContent: 'space-between' }}>
                          <div className="member-info">
                            <span className="member-name">{emp.employeeName}</span>
                            <span className="field-badge" style={{ marginLeft: 8 }}>{emp.employee_fields || '—'}</span>
                            {alreadyInTeam && <span className="already-added-badge">Added</span>}
                          </div>
                          <button
                            className="btn-action btn-add"
                            onClick={() => addMemberToTeam(emp.id)}
                            disabled={alreadyInTeam}
                          >
                            {alreadyInTeam ? 'Added' : 'Add'}
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Main Team List ──
  return (
    <div className="admin-container">
      <div className="header-banner mb-4">
        <div className="header-content">
          <div>
            <h1 className="header-title">Team Management </h1>
            <p className="header-subtitle">Create and manage teams</p>
          </div>
          <button className="btn-add-student" onClick={startCreate}>
            <span className="btn-icon"><FaPlus /></span> Create Team
          </button>
        </div>
      </div>

      {error && <div className="error-message"><FaTimesCircle style={{ marginRight: "6px" }} />{error}</div>}
      {success && <div className="success-message"><FaCheckCircle style={{ marginRight: "6px" }} />{success}</div>}

      {/* ── Create flow modal ── */}
      {createStep && (
        <div className="modal-overlay" onClick={() => setCreateStep(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h3 className="modal-title">{createStep === 'name' ? 'Enter Team Name' : 'Select Members'}</h3>
              <button className="modal-close" onClick={() => setCreateStep(null)} aria-label="Close team creation modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              {createStep === 'name' ? (
                <div className="form-group">
                  <label className="form-label">Team Name</label>
                  <input type="text" className="form-input" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="e.g., Development" />
                  <div className="modal-actions">
                    <button className="btn-cancel" onClick={() => setCreateStep(null)}>Cancel</button>
                    <button className="btn-submit" onClick={nextToMembers}>Next</button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>Select members for <strong>{newTeamName}</strong></p>
                  <div className="member-check-list">
                    {employees.map(emp => (
                      <label key={emp.id} className="member-check-item">
                        <input type="checkbox" checked={selectedMembers.includes(emp.id)}
                          onChange={() => toggleMemberSelection(emp.id)}
                          disabled={employeeTeamMap[emp.id] && !selectedMembers.includes(emp.id)} />
                        <span>{emp.employeeName}</span>
                        {employeeTeamMap[emp.id] && <span className="already-in-team">({employeeTeamMap[emp.id]})</span>}
                      </label>
                    ))}
                  </div>
                  <div className="modal-actions">
                    <button className="btn-cancel" onClick={() => setCreateStep('name')}>Back</button>
                    <button className="btn-submit" onClick={saveTeam} disabled={selectedMembers.length === 0}>Save Team</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit team name modal */}
      {editingTeam && (
        <div className="modal-overlay" onClick={() => setEditingTeam(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <h3 className="modal-title">Edit Team Name</h3>
              <button className="modal-close" onClick={() => setEditingTeam(null)} aria-label="Close edit team modal"><FaTimes /></button>
            </div>
            <div className="modal-body-custom">
              <div className="form-group">
                <label className="form-label">Team Name</label>
                <input type="text" className="form-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" onClick={() => setEditingTeam(null)}>Cancel</button>
                <button className="btn-submit" onClick={saveEdit}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Teams Table ── */}
      <div className="students-card">
        <div className="card-header-custom">
          <h3 className="card-title">Teams</h3>
          <p className="card-subtitle">{teams.length} teams</p>
        </div>
        <div className="table-wrapper">
          <table className="students-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Team Name</th>
                <th>Members</th>
                <th>Chat in Team</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teams.length === 0 ? (
                <tr><td colSpan="5" className="empty-state">No teams created yet</td></tr>
              ) : (
                teams.map((team, idx) => (
                  <tr key={team.id}>
                    <td>{idx + 1}</td>
                    <td><strong>{team.name}</strong></td>
                    <td><span className="team-count-badge">{team.member_count || 0} members</span></td>
                    <td>
                      <button className="btn-action btn-chat" onClick={() => openChat(team)}><FaComments style={{ marginRight: "6px" }} />Chat</button>
                    </td>
                    <td>
                      <div className="manage-buttons">
                        <button className="btn-action btn-view" onClick={() => setDetailTeam(team)}>View</button>
                        <button className="btn-action btn-edit" onClick={() => openEdit(team)}>Edit</button>
                        <button className="btn-action btn-delete" onClick={() => deleteTeam(team.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;